import type { BasketItem, Product } from '../types/order';
import { weightedLineTotal } from './pricing';

/**
 * Προϊόν «Βάρος + τιμή κιλού» (απόφαση ιδιοκτήτη 19/10/2026): ζυγιζόμενο ΚΑΙ ελεύθερης τιμής.
 *
 * Στο ταμείο (Take Away, Delivery) ο ταμίας δίνει ΠΡΩΤΑ το βάρος (το παράθυρο ζυγίσματος, χωρίς τιμή
 * καταλόγου) και ΜΕΤΑ την τιμή κιλού (ή λίτρου). Η γραμμή είναι ΑΚΡΙΒΩΣ όπως μια ζυγιζόμενη:
 * ποσότητα 1, weight, weightUnit = selling_unit || 'KG', totalPrice = σύνολο γραμμής — άρα το
 * παραστατικό περνά από τον ίδιο δρόμο (ο server βάζει ποσότητα = κιλά, μονάδα μέτρησης 2), και το
 * κατώτατο του server για ανοιχτή τιμή είναι το ποσό της γραμμής.
 *
 * Σύνολο = βάρος × τιμή κιλού με ΜΙΑ στρογγυλοποίηση (weightedLineTotal, ίδια με το κατώτατο).
 * Η πληκτρολογημένη τιμή κιλού μένει στη γραμμή (weightUnitPrice) μόνο για την εμφάνιση.
 */
export function isWeightedOpenPrice(product: Pick<Product, 'is_weight_variable' | 'is_open_price'> | null | undefined): boolean {
  return !!product && product.is_weight_variable === true && product.is_open_price === true;
}

/** Μονάδα της ζυγιζόμενης γραμμής, όπως στο addWeightVariableToBasket των οθονών. */
export function weightUnitOf(product: Pick<Product, 'selling_unit'>): string {
  return product.selling_unit || 'KG';
}

/**
 * Η γραμμή καλαθιού για βάρος + τιμή κιλού του ταμία. null για βάρος/τιμή που δεν είναι θετικοί
 * αριθμοί (τα παράθυρα δεν τα στέλνουν, αλλά καμία γραμμή 0 € ή NaN).
 */
export function weightedOpenPriceLine(product: Product, weight: number, pricePerUnit: number, now?: number): BasketItem | null {
  if (!(Number.isFinite(weight) && weight > 0) || !(Number.isFinite(pricePerUnit) && pricePerUnit > 0)) return null;
  const totalPrice = weightedLineTotal(pricePerUnit, weight);
  if (!Number.isFinite(totalPrice)) return null;
  return {
    id: `${product.id}-${now ?? Date.now()}`,
    product,
    selectedSize: null,
    quantity: 1, // όπως κάθε ζυγιζόμενο: το βάρος ορίζει το ποσό
    removedIngredients: [],
    addedExtras: [],
    specialInstructions: '',
    discount: undefined,
    totalPrice,
    price: totalPrice, // το DeliveryWithMap διαβάζει το price
    weight,
    weightUnit: weightUnitOf(product),
    weightUnitPrice: pricePerUnit,
  };
}
